// ============================================================================
// WebFlash — MCU Programmer & Debugger Types
// ============================================================================

// ── Cortex Type ──────────────────────────────────────────────────────────────

export type CortexType = 'CM0' | 'CM0+' | 'CM3' | 'CM4' | 'CM7';

// ── Core Register Enum ───────────────────────────────────────────────────────

export enum CoreRegister {
  R0 = 0, R1 = 1, R2 = 2, R3 = 3,
  R4 = 4, R5 = 5, R6 = 6, R7 = 7,
  R8 = 8, R9 = 9, R10 = 10, R11 = 11, R12 = 12,
  SP = 13, LR = 14, PC = 15,
  xPSR = 16,
}

export type RegisterMap = Record<string, number>;

// ── Target Info ──────────────────────────────────────────────────────────────

export interface TargetInfo {
  idcode: number;
  partNumber: string;
  flashSize: number;       // bytes
  ramSize: number;         // bytes
  cpuType: CortexType;
  hasHardwareCRC: boolean;
}

// ── Flash Progress & Result ──────────────────────────────────────────────────

export type FlashStage = 'erase' | 'program' | 'verify';

export interface FlashProgress {
  stage: FlashStage;
  current: number;         // bytes processed
  total: number;           // total bytes
  percent: number;         // 0–100
}

export interface FlashResult {
  success: boolean;
  stages: {
    erase:   { ok: boolean; durationMs: number };
    program: { ok: boolean; durationMs: number; bytesWritten: number };
    verify:  { ok: boolean; durationMs: number; expectedCRC: string; actualCRC: string };
  };
  totalDurationMs: number;
}

// ── Option Bytes & Fault Registers ───────────────────────────────────────────

export interface OptionBytes {
  rdpLevel: 0 | 1 | 2;
  raw: Uint8Array;
  [key: string]: unknown;
}

export interface FaultRegisters {
  cfsr: number;            // Configurable Fault Status Register
  hfsr: number;            // HardFault Status Register
  mmfar: number;           // MemManage Fault Address
  bfar: number;            // BusFault Address
  dfsr: number;            // Debug Fault Status Register
  pc: number;
  lr: number;
  sp: number;
}

// ── Probe Connection Interface ───────────────────────────────────────────────

export type ProbeEvent = 'connected' | 'disconnected' | 'error';

export interface ProbeConnection {
  connect(): Promise<TargetInfo>;
  disconnect(): Promise<void>;
  isConnected(): boolean;

  halt(): Promise<void>;
  resume(): Promise<void>;
  reset(halt?: boolean): Promise<void>;

  readMemory(address: number, length: number): Promise<Uint8Array>;
  writeMemory(address: number, data: Uint8Array): Promise<void>;

  readCoreRegister(reg: CoreRegister): Promise<number>;
  writeCoreRegister(reg: CoreRegister, value: number): Promise<void>;
  readAllRegisters(): Promise<RegisterMap>;

  eraseFlash(startAddr: number, length: number): Promise<void>;
  flashFirmware(
    firmware: Uint8Array,
    baseAddr: number,
    onProgress?: (p: FlashProgress) => void
  ): Promise<FlashResult>;

  readFlash(onProgress?: (percent: number) => void): Promise<Uint8Array>;
  readRegion(address: number, length: number): Promise<Uint8Array>;
  readOptionBytes(): Promise<OptionBytes>;
  readFaultRegisters(): Promise<FaultRegisters>;

  on(event: ProbeEvent, handler: (...args: unknown[]) => void): void;
}

// ── Target Definition ────────────────────────────────────────────────────────

export interface FlashSector {
  address: number;
  size: number;
}

export interface TargetDefinition {
  name: string;
  partNumber: string;
  idcodes: number[];
  cpuType: CortexType;
  flashBase: number;
  flashSize: number;
  ramBase: number;
  ramSize: number;
  sectors: FlashSector[];
  hasHardwareCRC: boolean;
  crcPeripheralAddr?: number;
}

// ── Firmware Types ───────────────────────────────────────────────────────────

export interface FirmwareProduct {
  name: string;
  description?: string;
  versions: string[];
}

export interface BuildInfo {
  toolchain: string;
  gitCommit: string;
  gitBranch: string;
  buildDate: string;
  crc32: string;
  sha256: string;
  size: number;
}

export interface FirmwareVersion {
  id: string;
  product: string;
  version: string;
  buildInfo?: BuildInfo;
  fileTypes: string[];    // 'hex' | 'elf' | 'bin' | 'map' | 'lst'
  createdAt?: string;
}

// ── Readback & Verification ──────────────────────────────────────────────────

export interface ReadbackResult {
  data: Uint8Array;
  address: number;
  length: number;
  timestamp: string;
}

export interface VerificationResult {
  passed: boolean;
  method: 'crc' | 'readback';
  expectedCRC?: string;
  actualCRC?: string;
  mismatchAddress?: number;
}

// ── Flash Log (Backend) ──────────────────────────────────────────────────────

export type FlashMode = 'webusb' | 'backend' | 'webhid';
export type CRCMethod = 'hardware' | 'software' | 'readback';

export interface FlashLog {
  id: string;
  serialNumber: string;
  targetPart: string;
  firmwareVersion: string;
  firmwareProduct: string;
  flashMode: FlashMode;
  crcMethod: CRCMethod;
  success: boolean;
  expectedCRC: string;
  actualCRC: string;
  totalDurationMs: number;
  eraseDurationMs: number;
  programDurationMs: number;
  verifyDurationMs: number;
  bytesWritten: number;
  operatorName: string;
  notes?: string;
  createdAt: string;
}

export interface CreateFlashLogRequest {
  serialNumber: string;
  targetPart: string;
  firmwareVersion: string;
  firmwareProduct: string;
  flashMode: FlashMode;
  crcMethod: CRCMethod;
  success: boolean;
  expectedCRC: string;
  actualCRC: string;
  totalDurationMs: number;
  eraseDurationMs: number;
  programDurationMs: number;
  verifyDurationMs: number;
  bytesWritten: number;
  notes?: string;
}

export interface FlashLogFilter {
  serialNumber?: string;
  firmwareProduct?: string;
  success?: boolean;
  limit?: number;
  offset?: number;
}

// ── Status Labels & Colors ───────────────────────────────────────────────────

export const FLASH_STAGE_LABELS: Record<FlashStage, string> = {
  erase: 'Erasing',
  program: 'Programming',
  verify: 'Verifying',
};

export const FLASH_STAGE_COLORS: Record<FlashStage, string> = {
  erase: 'text-yellow-600',
  program: 'text-blue-600',
  verify: 'text-green-600',
};
